import type { ApolloCache, Reference } from '@apollo/client';
import gql from 'graphql-tag';
import { partition, uniq } from 'lodash';
import type { Apollo } from 'src/gql/generated';

const ARTIFACT_WORKFLOW_FRAGMENT = gql`
  fragment ArtifactWorkflowFields on Workflow {
    id
    name
  }
`;

export interface WorkflowArtifactCreatedEventForCache {
  agentMessageId: string;
  artifactCreatedAt: string;
  kindList: Apollo.AgentMessageJoinKind[];
  threadId: string;
  workflowId: string;
  workflowName: string;
}

type ThreadArtifactCacheRow = NonNullable<
  Apollo.GetAgentThreadQuery['agentThread']
>['artifacts'][number];
type ThreadWorkflowArtifactCacheRow = Extract<
  ThreadArtifactCacheRow,
  { __typename: 'ThreadWorkflowArtifact' }
>;

export function applyWorkflowArtifactCreatedEventToCache(
  cache: ApolloCache,
  event: WorkflowArtifactCreatedEventForCache
): void {
  const row: ThreadWorkflowArtifactCacheRow = {
    __typename: 'ThreadWorkflowArtifact',
    agentMessageId: event.agentMessageId,
    createdAt: event.artifactCreatedAt,
    kindList: event.kindList,
    workflow: {
      __typename: 'Workflow',
      id: event.workflowId,
      name: event.workflowName,
    },
  };

  cache.writeFragment({
    data: row.workflow,
    fragment: ARTIFACT_WORKFLOW_FRAGMENT,
    id: cache.identify({ __typename: 'Workflow', id: row.workflow.id }),
  });
  cache.modify({
    fields: {
      artifacts(existing = [], { readField }) {
        if (!Array.isArray(existing)) return existing;

        const [matchingRows, remainingRows] = partition(existing, artifact => {
          const workflowRef = readField<Reference>(
            'workflow' satisfies keyof ThreadWorkflowArtifactCacheRow,
            artifact
          );
          return readField('id', workflowRef) === event.workflowId;
        });
        const existingKindList = matchingRows.flatMap(
          existingRow =>
            readField<readonly Apollo.AgentMessageJoinKind[]>(
              'kindList' satisfies keyof ThreadWorkflowArtifactCacheRow,
              existingRow
            ) ?? []
        );

        return [
          {
            ...row,
            kindList: uniq([...existingKindList, ...event.kindList]),
          },
          ...remainingRows,
        ];
      },
    },
    id: cache.identify({ __typename: 'AgentThread', id: event.threadId }),
  });
}
